import React, { FC, ReactNode, useEffect, useRef, useMemo, useCallback } from 'react';
import { useAnalytics } from '@johnlewispartnership/wtr-website-analytics';
import { useExperiments } from '@johnlewispartnership/wtr-website-decisioner-client/express';
import TrackedExperimentsContext, { ExperimentView } from './TrackedExperimentsContext';

const MONETATE_DEFAULT_VARIANT = 'default';

const TrackedExperimentsProvider: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { trackEvent } = useAnalytics();
  const {
    experimentsContext: { experiments },
  } = useExperiments();

  const toTrack = useRef(new Map());
  const tracked = useRef(new Map());

  const isExperimentLive = useCallback(
    (id: string): boolean => Object.keys(experiments || {}).includes(id),
    [experiments],
  );

  const trackExperimentViewEvent = useCallback(
    ({ id, variant, trackAll }: ExperimentView): void => {
      if (!isExperimentLive(id)) {
        // Mark it as tracked just to save re-checking if it's live again each time
        tracked.current.set(id, 1);
        return;
      }

      if (trackAll || !tracked.current.has(id)) {
        trackEvent({
          event: 'monetate_fe_experiment_viewed',
          monetate_fe_experiment: {
            id,
            variant,
          },
        });
        tracked.current.set(id, 1);
      }
    },
    [trackEvent, isExperimentLive],
  );

  const getExperimentById = useCallback(
    (id: string, { manualTracking = false } = {}) => {
      const exp = experiments?.[id];
      const variant = exp?.variant ?? MONETATE_DEFAULT_VARIANT;

      if (!manualTracking) {
        toTrack.current.set(id, variant);
      }

      return {
        variant,
        payload: exp?.payload,
        experienceLabel: exp?.experienceLabel,
        variantLabel: exp?.variantLabel,
        trackExperimentView: (trackAll = false) => {
          // Manual tracking can be useful e.g. for content below the fold which
          // we don't want to deem as having been viewed automatically on page load
          if (manualTracking) {
            trackExperimentViewEvent({ id, variant, trackAll });
          }
        },
      };
    },
    [experiments, trackExperimentViewEvent],
  );

  const contextValue = useMemo(
    () => ({
      getExperimentById,
      trackExperimentViewEvent,
    }),
    [getExperimentById, trackExperimentViewEvent],
  );

  useEffect(() => {
    toTrack.current.forEach((variant, id) => {
      if (!tracked.current.has(id)) {
        trackExperimentViewEvent({ id, variant });
      }
    });
  });

  return (
    <TrackedExperimentsContext.Provider value={contextValue}>
      {children}
    </TrackedExperimentsContext.Provider>
  );
};

export default TrackedExperimentsProvider;
